import config from 'constants/config'
import { ITEM_STATUS_AWAITING_DATES, ITEM_STATUS_CANCELLED, ITEM_STATUS_CREATED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import moment from 'moment'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getLatestCompletedReservation } from 'lib/order/orderUtils'
import { getIsMaldivesCharterBundleOrder } from 'lib/order/charterUpgradeUtils'
import { isItemUpcoming } from 'lib/order/isUpcoming'
import { ISO_DATE_FORMAT, ACCOMMODATION_TIME_FORMAT } from 'constants/dateFormats'
import { hasApprovedBNBLRefundRequest } from 'lib/order/refundRequestUtils'
import { isSelfServiceRebookAllowed } from 'lib/offer/isSelfServiceRebookAllowed'
import { canBeUpgraded } from '../HotelItemSummaryUtils'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import { orderHasInsuranceBookingProtection } from 'lib/order/orderInsuranceUtils'
import { canRefundItem } from 'lib/order/orderItemUtils'
import { PACKAGE_UPGRADE_ALLOWED_OFFER_IDS } from 'lib/offer/packageUpgradeAllowlist'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'

export interface OrderHotelChangeDatesConfig {
  canDateChange: boolean
  /**
   * exact date when daysBeforeCheckInChangesDisallowed is reached
   */
  policyDeadline: string
  /**
   * Whether or not the user can request a date change through outsidePolicy DatesRequest
   * when offer's daysBeforeCheckInChangesDisallowed date is reached
   */
  canRequestDateChange: boolean
  /**
   * Formatted display string for the change dates deadline.
   * Accounts for both policyDeadline and the offer's schedule (for Always On offers).
   * Only set when canDateChange is true.
   */
  changeDatesDeadlineDisplay?: string
  /**
   * Formatted display string for the extend dates deadline.
   * Derived from min(offerScheduleEnd, effectiveChangeDatesEnd).
   * Only set when canDateChange is true and offer has an online purchase schedule.
   */
  extendDatesDeadlineDisplay?: string
}

export const canAddFlights = (order: App.Order, geo: App.GeoState, offerFlightsEnabled: boolean) => {
  if (!config.BNBL_FLIGHT_BOOKING_ENABLED) return false
  if (!offerFlightsEnabled) return false
  if (!geo.currentRegionHasFlights) return false

  const alreadyHasFlights = order.flightItems.some(
    ({ status }) => status !== ITEM_STATUS_CANCELLED,
  )
  if (alreadyHasFlights) return false

  const latestReservation = getLatestCompletedReservation(order)
  if (latestReservation && !isItemUpcoming(latestReservation)) return false

  if (order.items?.some(i => i.canBookDates)) return false

  const validStatuses = [
    ITEM_STATUS_AWAITING_DATES,
    ITEM_STATUS_CREATED,
    ITEM_STATUS_COMPLETED,
  ]
  const validAccomItems = order.items.filter(({ status }) => validStatuses.includes(status))
  return (validAccomItems.length === 1)
}

export const changeDatesUntilStr = (checkInDate: string, changeDatesLimit: number) => {
  const intl = getCurrentIntl()
  const formattedDate = moment.utc(checkInDate).subtract(changeDatesLimit, 'day').format('ll')
  return intl.formatMessage({
    id: 'JjmwPq5NlqkBOUstRqdo',
    defaultMessage: '{date} ({count, plural, one {# day} other {# days}} before check-in).',
    description: 'Deadline for changing hotel booking dates, showing date and days before check-in',
  }, { date: formattedDate, count: changeDatesLimit })
}

export function canShowBookByDates(canBookDates: boolean, bookByDate: string) {
  if (!canBookDates) return false
  return moment.utc(bookByDate).subtract(2, 'month').isAfter(moment.utc('2021-01-01'))
}

export const getCapacityPath = (item: App.OrderItem, order: App.Order, originAirportCode?: string) => ({
  orderItemId: item.id,
  orderId: order.id,
  originAirportCode,
})

export const hasActiveReservation = (item: App.OrderItem) => {
  if (item.reservationError) return false
  if (!item.reservation) return false
  if (item.status === 'cancelled') return false
  return true
}

const allowedDateChangeTypes = new Set<App.OfferType>(['hotel', 'tactical_ao_hotel'])
const allowedInstallmentDateChangeTypes = new Set<App.OfferType>(['hotel'])
const allowedDepositDateChangeTypes = new Set<App.OfferType>(['hotel', 'tour'])
export function isDateChangeAllowed(
  confirmation: boolean | undefined,
  item: App.OrderItem | undefined,
  numberOfDaysBeforeBookingIsLockedIn: number,
  changeDatesLimitReached: boolean,
  order: App.Order,
  cancellationConfig: App.OrderItemHotelCancellationConfig,
  offer: App.Offer,
): boolean {
  if (!item) return false
  if (item.isCompedPackage) return false
  if (!allowedDateChangeTypes.has(item.offer.type)) return false
  if (confirmation) return false
  if (!item.reservation) return false
  if (item.canBookDates) return false
  if (item.status === ITEM_STATUS_CANCELLED) return false
  if (order.hasFlight) return false
  if (changeDatesLimitReached) return false
  if (order.depositDetails && !allowedDepositDateChangeTypes.has(item.offer.type)) return false
  if (order.instalmentDetails && !allowedInstallmentDateChangeTypes.has(item.offer.type)) return false
  if (item.offer.type === OFFER_TYPE_ALWAYS_ON && !cancellationConfig.canRefund) return false
  if (item.offer.type === OFFER_TYPE_ALWAYS_ON && !scheduleIsCurrent(offer.onlinePurchaseSchedule)) return false
  if (order.reserveForZeroDetails?.isActive) return false
  if (!config.accommodation.allowDateChangeOnOrder) return false
  if (!numberOfDaysBeforeBookingIsLockedIn || !item.reservation) return false

  return true
}

export function getHotelChangeDatesConfig(
  confirmation: boolean | undefined,
  item: App.OrderItem | undefined,
  order: App.Order,
  offer: App.Offer,
  cancellationConfig: App.OrderItemHotelCancellationConfig,
) {
  const offerScheduleEnd = offer?.onlinePurchaseSchedule?.end ? moment(offer.onlinePurchaseSchedule.end).format(ISO_DATE_FORMAT) : undefined

  const configs: OrderHotelChangeDatesConfig = {
    canDateChange: false,
    policyDeadline: '',
    canRequestDateChange: false,
  }

  if (!item) return configs

  const numberOfDaysBeforeBookingIsLockedIn = offer?.daysBeforeCheckInChangesDisallowed || 0
  const changeDatesLimitReached = offer?.numberOfDateChangesAllowed !== 'Unlimited' && parseInt(offer.numberOfDateChangesAllowed || '') <= item.numberOfDateChanges

  const dateChangeAllowed = isDateChangeAllowed(confirmation, item, numberOfDaysBeforeBookingIsLockedIn, changeDatesLimitReached, order, cancellationConfig, offer)
  const hasOutsidePolicyDatesRequests = order.outsidePolicyDatesRequests[item.id] || order.outsidePolicyDatesRequests[item.downgradedFromId ?? '']

  if (item.reservation && dateChangeAllowed) {
    const finalDateAllowed = moment(item.reservation.startDate).subtract(numberOfDaysBeforeBookingIsLockedIn, 'days')
    const isWithinChangeDatesPolicyDeadline = moment().startOf('day').isSameOrBefore(finalDateAllowed)
    configs.policyDeadline = finalDateAllowed.format(ISO_DATE_FORMAT)

    const isAlwaysOn = item.offer.type === OFFER_TYPE_ALWAYS_ON
    const effectiveChangeDatesEnd = isAlwaysOn && offerScheduleEnd && moment(offerScheduleEnd).isBefore(finalDateAllowed)
      ? offerScheduleEnd
      : configs.policyDeadline

    configs.canDateChange = isWithinChangeDatesPolicyDeadline && !hasOutsidePolicyDatesRequests && !hasApprovedBNBLRefundRequest(order, item)
    configs.canRequestDateChange = !isWithinChangeDatesPolicyDeadline && !hasOutsidePolicyDatesRequests && !hasApprovedBNBLRefundRequest(order, item)

    if (configs.canDateChange) {
      configs.changeDatesDeadlineDisplay = moment(effectiveChangeDatesEnd).format(ACCOMMODATION_TIME_FORMAT)
      // Extend deadline can differ from change deadline for non-AO offers (capped by schedule end).
      // For AO offers, the schedule is already factored into effectiveChangeDatesEnd, so both match.
      if (offerScheduleEnd) {
        const effectiveExtendDatesEnd = moment(offerScheduleEnd).isBefore(effectiveChangeDatesEnd) ? offerScheduleEnd : effectiveChangeDatesEnd
        configs.extendDatesDeadlineDisplay = moment(effectiveExtendDatesEnd).format(ACCOMMODATION_TIME_FORMAT)
      }
    }
  }

  return configs
}

const allowedExtendDateTypes = new Set<App.OfferType>(['hotel', 'tactical_ao_hotel'])
export function isExtendDatesAllowed(
  item: App.OrderItem,
  offer: App.Offer,
  order: App.Order,
  cancellationConfig?: App.OrderItemHotelCancellationConfig,
): boolean {
  if (item.status === ITEM_STATUS_CANCELLED) return false
  if (!allowedExtendDateTypes.has(offer.type)) return false
  if (!isSelfServiceRebookAllowed(offer, order, cancellationConfig)) return false
  if (offer.type === 'hotel' && item.reservation?.channelManager !== 'self-managed') return false
  return true
}

/**
 * Checks basic item state validations for upgrade eligibility.
 * This is similar to canRefundItem but doesn't check canRefund policy
 * since canUpgradePackage handles that with relaxed criteria for flash hotels.
 */
function canUpgradeItem(item: App.OrderItem, order: App.Order) {
  // can't upgrade a cancelled item
  return item.status !== 'cancelled' &&
    // can't already have a refund
    !order.refunds.some(refund => refund.itemId === item.id) &&
    // can't have a refund request against it
    !order.refundRequests[item.id] &&
    // Block upgrade if insurance claim is pending
    !order.locked
}

export function isPackageUpgradeAllowed(
  item: App.OrderItem,
  order: App.Order,
  offer: App.Offer,
  hasUnpaidDepositAmount: boolean,
  hasUnpaidInstalment: boolean,
  cancellationConfig?: App.OrderItemHotelCancellationConfig,
) {
  if (order.locked) return false

  const isComped = item.isCompedPackage
  // Payment schedule orders can now upgrade - adjustment is handled via payment schedule API
  const isReserveForZero = order.reserveForZeroDetails?.isActive
  const isMaldivesCharterOrder = getIsMaldivesCharterBundleOrder(order)
  const isGiftOrder = !!order.orderGift
  // isSelfServiceRebookAllowed uses canUpgradePackage for allowlisted offers, canAutoRefund for others
  const isRebookAllowed = isSelfServiceRebookAllowed(offer, order, cancellationConfig)
    || (isMaldivesCharterOrder && !isGiftOrder)
  const isUpgradeable = canBeUpgraded(item)
  const isFlightBundled = isFlightForceBundled(offer)
  const hasApprovedRefund = hasApprovedBNBLRefundRequest(order, item)
  const hasBookingProtection = orderHasInsuranceBookingProtection(order)

  if (PACKAGE_UPGRADE_ALLOWED_OFFER_IDS.has(offer.id)) {
    // For allowlisted offers: use canUpgradeItem (relaxed check - no canRefund policy check)
    // The canUpgradePackage flag (checked in isSelfServiceRebookAllowed) already handles
    // the relaxed upgrade eligibility criteria for flash hotels
    if (!canUpgradeItem(item, order)) return false
  } else {
    // For all other offers: use pre-existing canRefundItem + canRefund checks
    if (!canRefundItem(item, order, cancellationConfig)) return false
    if (!cancellationConfig?.canRefund) return false
  }

  return !isComped
    && !hasUnpaidDepositAmount
    && !hasUnpaidInstalment
    && !isReserveForZero
    && isRebookAllowed
    && isUpgradeable
    && !isFlightBundled
    && !hasApprovedRefund
    && !hasBookingProtection
}

// TODO: Temporary BNBL bypass for conflict in middle east - Remove when CS team gives go ahead
const LetPutOnHoldMiddleEastConflict = new Set([
  '006RF00000MgtwHYAR',
  '006RF00000HqeKvYAJ',
  '006RF00000OIRVOYA5',
  '006RF00000OxkFWYAZ',
  '006RF00000E5O9jYAF',
  '006RF00000JpqT3YAJ',
  '006RF00000Fnpn7YAB',
  '006RF00000KEECTYA5',
  '006RF00000FPHufYAH',
  '006RF00000PxnuNYAR',
  '006RF00000JsxMkYAJ',
  '006RF00000HSvO9YAL',
  '006RF00000OXwNZYA1',
  '006RF00000GgjKTYAZ',
  '006RF00000MU2J4YAL',
  '006RF00000JBxppYAD',
  '006RF00000OyV6LYAV',
  '006RF00000Gb0BCYAZ',
  '006RF00000O3hsFYAR',
  '006RF00000Ol2esYAB',
  '006RF00000K4csoYAB',
  '006RF00000KpuRFYAZ',
  '006RF00000LfambYAB',
  '006RF00000K9AhOYAV',
  '006RF00000LkFftYAF',
  '006RF00000DjWkEYAV',
  '006RF00000MJXd9YAH',
  '006RF00000OqAVJYA3',
  '006RF00000J0AHQYA3',
  '006RF00000DdyCwYAJ',
  '006Ii000002HypJIAS',
  '006RF00000IpzLdYAJ',
  '006Ii000002J2t9IAC',
  '006RF00000KKvGLYA1',
  '006RF00000Cffl4YAB',
  '006RF00000IsO6DYAV',
  '0062y00000JTEhvAAH',
  '006RF00000MKB14YAH',
  '006RF00000Osb82YAB',
  '006RF00000Fpxv7YAB',
  '006RF00000OFItFYAX',
  '006RF00000Lf6v0YAB',
  '006RF00000ORdIoYAL',
  '006RF00000I2cjZYAR',
  '006RF00000DdtwRYAR',
  '006RF00000OsQHSYA3',
  '006RF00000HEpnFYAT',
  '006RF00000OlWD7YAN',
  '006RF00000FfTa6YAF',
  '006RF00000OW53aYAD',
  '006RF00000GIe5pYAD',
  '006RF00000PmWP2YAN',
  '006RF00000ItvY3YAJ',
  '006RF00000FmHnVYAV',
  '006Ii000002HzH8IAK',
  '006RF00000IfcGgYAJ',
  '006RF00000PaMWjYAN',
  '006RF00000K9UHtYAN',
  '006RF00000MJc8CYAT',
  '006RF00000NvlJWYAZ',
])

export function canPutOnHold(
  item: App.OrderItem,
  order: App.Order,
  offerType: App.OfferType | undefined,
  canDateChange: boolean,
) {
  return (config.CONVERT_ORDER_TO_BNBL && canDateChange && !item.isCompedPackage && order.bookingProtectionItems.length === 0 && offerType === 'hotel')
}

/**
 * Check if the item should be put on hold due to Middle East conflict.
 * Applies to reservations with a start date within the next 7 days.
 * TODO: Temporary - Remove when CS team gives go ahead
 */
export function putOnHoldMiddleEastConflict(item: App.OrderItem): boolean {
  if (item.reservation && LetPutOnHoldMiddleEastConflict.has(item.offerId)) {
    return moment(item.reservation.startDate).isBefore(moment().add(7, 'days').endOf('day'))
  }
  return false
}
